<template>
  <section class="border border-gray-200 rounded-lg bg-white h-[500px] flex flex-col">
    <div class="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200">
      <div>
        <h2 class="text-base font-semibold text-gray-900">Core latency issues</h2>
        <p class="text-xs text-gray-600 mt-0.5">
          Ranked by aggregate wait beyond 2 seconds; trends require 100 calls in both windows.
        </p>
      </div>
      <div class="flex items-center gap-1">
        <button
          v-for="option in trendOptions"
          :key="option.value"
          type="button"
          class="h-8 px-3 rounded text-xs border"
          :class="trendFilter === option.value
            ? 'border-primary-500 bg-primary-100 text-primary-700 font-semibold'
            : 'border-gray-200 text-gray-700 hover:bg-gray-50'"
          @click="trendFilter = option.value"
        >{{ option.label }}</button>
      </div>
    </div>

    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <LoadingIndicator message="Loading endpoint latency..." />
    </div>
    <div v-else-if="error" class="flex-1 overflow-auto p-5 text-sm text-red-dark">
      {{ error }}
    </div>
    <div v-else-if="!sortedRows.length" class="flex-1 p-5 text-sm text-gray-600">
      No endpoints match these filters.
    </div>
    <div v-else class="flex-1 min-h-0 overflow-auto">
      <table class="w-full text-[13px]" style="border-collapse: collapse">
        <colgroup>
          <col style="width: 7%" />
          <col style="width: 31%" />
          <col style="width: 8%" />
          <col style="width: 9%" />
          <col style="width: 9%" />
          <col style="width: 9%" />
          <col style="width: 9%" />
          <col style="width: 8%" />
          <col style="width: 10%" />
        </colgroup>
        <thead>
          <tr class="bg-gray-50 border-b border-gray-200 sticky top-0 z-[1]">
            <Latency_SortHeader
              v-for="(column, index) in columns"
              :key="column.key"
              :sort-key="column.key"
              :active-key="sortKey"
              :direction="sortDir"
              :th-class="headerClass(index)"
              @sort="sortBy"
            >{{ column.label }}</Latency_SortHeader>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in sortedRows" :key="`${row.method}:${row.endpoint}`" class="border-b border-gray-100">
            <td class="pl-4 pr-2 py-2.5 align-middle font-medium text-gray-700">{{ row.method }}</td>
            <td class="px-2 py-2.5 align-middle font-mono text-xs text-gray-900 break-all">{{ row.endpoint }}</td>
            <td class="px-2 py-2.5 align-middle text-gray-700">{{ formatCount(row.calls) }}</td>
            <td class="px-2 py-2.5 align-middle text-gray-900 font-medium">{{ formatDuration(row.p90_ms) }}</td>
            <td class="px-2 py-2.5 align-middle" :class="trendClass(row, 'p90_delta_pct')">
              {{ trendText(row, 'p90_delta_pct') }}
            </td>
            <td class="px-2 py-2.5 align-middle text-gray-900 font-medium">{{ formatDuration(row.p95_ms) }}</td>
            <td class="px-2 py-2.5 align-middle" :class="trendClass(row, 'p95_delta_pct')">
              {{ trendText(row, 'p95_delta_pct') }}
            </td>
            <td class="px-2 py-2.5 align-middle text-gray-700">{{ formatCount(row.over_10s) }}</td>
            <td class="pl-2 pr-4 py-2.5 align-middle text-gray-900 font-medium">
              {{ formatWait(row.excess_wait_minutes) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import Latency_SortHeader from './Latency_SortHeader.vue';
import {
  compareRows, deltaClass, formatCount, formatDelta, formatDuration, formatWait,
} from './latency_format';

const COLUMNS = [
  { key: 'method', label: 'Method' },
  { key: 'endpoint', label: 'Endpoint' },
  { key: 'calls', label: 'Calls' },
  { key: 'p90_ms', label: 'P90' },
  { key: 'p90_delta_pct', label: 'P90 change' },
  { key: 'p95_ms', label: 'P95' },
  { key: 'p95_delta_pct', label: 'P95 change' },
  { key: 'over_10s', label: '≥10s' },
  { key: 'excess_wait_minutes', label: 'Excess wait' },
];

export default {
  name: 'LatencyEndpointTable',
  components: { Latency_SortHeader, LoadingIndicator },
  props: {
    rows: { type: Array, required: true },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
  },
  data() {
    return {
      columns: COLUMNS,
      sortKey: 'excess_wait_minutes',
      sortDir: 'desc',
      trendFilter: 'all',
      trendOptions: [
        { value: 'all', label: 'All' },
        { value: 'regressing', label: 'Regressing' },
        { value: 'improving', label: 'Improving' },
        { value: 'mixed', label: 'Mixed' },
      ],
    };
  },
  computed: {
    filteredRows() {
      if (this.trendFilter === 'all') return this.rows;
      return this.rows.filter((row) => this.trendDirection(row) === this.trendFilter);
    },
    sortedRows() {
      return [...this.filteredRows].sort(compareRows(this.sortKey, this.sortDir));
    },
  },
  methods: {
    formatCount,
    formatDuration,
    formatWait,
    sortBy(key) {
      if (this.sortKey !== key) {
        this.sortKey = key;
        this.sortDir = 'asc';
      } else {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      }
    },
    headerClass(index) {
      const edge = index === 0 ? 'pl-4' : index === this.columns.length - 1 ? 'pr-4' : '';
      return `px-2 py-2.5 text-left ${edge}`;
    },
    trendDirection(row) {
      if (!row.trend_eligible) return 'mixed';
      const p90 = row.p90_delta_pct;
      const p95 = row.p95_delta_pct;
      if (p90 > 0 && p95 > 0) return 'regressing';
      if (p90 < 0 && p95 < 0) return 'improving';
      return 'mixed';
    },
    trendText(row, key) {
      return row.trend_eligible ? formatDelta(row[key]) : 'Low sample';
    },
    trendClass(row, key) {
      return row.trend_eligible ? deltaClass(row[key]) : 'text-gray-400 italic';
    },
  },
};
</script>
